import { normalizeRanges, range, subtractRanges, totalDuration, type TimeRange } from '../time/ranges';

export type ScanPrecision = 'fast' | 'thorough';

export interface ScanPlan {
  /** Detection runs per second of video; the tracker fills the frames in between. */
  readonly fps: number;
  /** Long edge (px) frames are decoded at for analysis. */
  readonly longEdge: number;
}

/**
 * Picks sampling density by precision and length: long videos are sampled a bit
 * more sparsely so an hour of footage still scans in reasonable time.
 */
export function planScan(durationSec: number, precision: ScanPrecision): ScanPlan {
  const long = durationSec > 20 * 60;
  if (precision === 'thorough') return { fps: long ? 5 : 8, longEdge: 1920 };
  return { fps: long ? 3 : 5, longEdge: 1280 };
}

/** Evenly spaced sample timestamps in `[start, end)`. Always includes `start`. */
export function sampleTimes(start: number, end: number, fps: number): number[] {
  if (!(end > start) || !(fps > 0)) return [];
  const step = 1 / fps;
  const count = Math.ceil((end - start) / step - 1e-9);
  return Array.from({ length: count }, (_, i) => start + i * step);
}

/** Analysis frame size: display aspect, long edge capped, never upscaled. */
export function analysisSize(width: number, height: number, longEdge: number): { width: number; height: number } {
  const scale = Math.min(1, longEdge / Math.max(width, height));
  return { width: Math.max(2, Math.round(width * scale)), height: Math.max(2, Math.round(height * scale)) };
}

/** Seconds also scanned around each part: the tracker is already sure of a face when the part starts. */
export const SCAN_MARGIN_SEC = 1;
/** Parts closer than this are scanned in one go: seeking costs more than decoding a short gap. */
export const SCAN_JOIN_GAP_SEC = 2;
/** Unscanned bits shorter than a frame can't show anyone (rounding leftovers, not parts). */
export const MIN_UNSCANNED_SEC = 0.01;

/** The parts of `wanted` (a video's own seconds) no scan has looked at yet, ignoring slivers. */
export const unscannedRanges = (wanted: readonly TimeRange[], covered: readonly TimeRange[]): TimeRange[] =>
  subtractRanges(wanted, covered).filter((r) => r.end - r.start >= MIN_UNSCANNED_SEC);

/**
 * What to decode to cover `wanted` (the parts left in the edit) where `covered`
 * has not been scanned yet: each part with a margin on both sides, near parts
 * joined, inside `bounds`. Empty when everything wanted was scanned already.
 */
export function scanRanges(wanted: readonly TimeRange[], covered: readonly TimeRange[], bounds: TimeRange): TimeRange[] {
  const missing = unscannedRanges(wanted, covered);
  const padded = missing.map((r) => range(Math.max(bounds.start, r.start - SCAN_MARGIN_SEC), Math.min(bounds.end, r.end + SCAN_MARGIN_SEC)));
  const joined: TimeRange[] = [];
  for (const r of normalizeRanges(padded)) {
    const last = joined.at(-1);
    if (last && r.start - last.end < SCAN_JOIN_GAP_SEC) joined[joined.length - 1] = range(last.start, r.end);
    else joined.push(r);
  }
  return joined;
}

/** Sample timestamps through every range, in time order. */
export const sampleRanges = (ranges: readonly TimeRange[], fps: number): number[] => normalizeRanges(ranges).flatMap((r) => sampleTimes(r.start, r.end, fps));

/** Seconds of video a set of ranges asks to scan (what the sampling density is chosen by). */
export const scanLength = (ranges: readonly TimeRange[]): number => totalDuration(normalizeRanges(ranges));
